/**
 * The world list in settings (D241, D262, D337). Each idle world can be played: the one open is
 * marked Playing and the others have Open, which switches to them. "Start a new world" shows
 * while the player has fewer than `worldsPerPlayer` of them and runs the first run's naming and
 * seed. Any idle world can be deleted, which frees its place. A world from the old game is kept
 * and can be looked at: it opens a short read-only summary through `GET /worlds/:id/view`, and
 * it does not count towards the limit.
 */
import { useEffect, useState } from 'preact/hooks';
import {
  DELETE_WORLD,
  deleteIdleWorldLoses,
  DELETE_WORLD_CONFIRM,
  DELETE_WORLD_INPUT,
  DELETE_WORLD_NEEDS_NAME,
  deleteWorldLabel,
  deleteWorldTitle,
} from '../game/copy.ts';
import { DeleteWorldBody } from '../screens/DeleteWorldSheet.tsx';
import { Button } from '../ui/Button.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { RenameWorldSheet } from './RenameWorldSheet.tsx';
import { ThemeSwatch } from './ThemeSwatch.tsx';
import { WorldDetails, WorldRows, SMALL } from './WorldRows.tsx';
import { idleIncome } from '@hedgerow/sim';
import { coins } from './notation.ts';
import { yearHeader } from './view.ts';
import { idleContent, useIdle } from './store.ts';
import { idleSync, type ArchivedWorldView, type IdleWorldEntry } from './sync.ts';

/** A date as the player reads it: 4 October 2026. */
const day = (t: number) =>
  new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

export function WorldList({ worlds }: { worlds: IdleWorldEntry[] }) {
  const [viewing, setViewing] = useState<IdleWorldEntry | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState<IdleWorldEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async (f: IdleWorldEntry) => {
    setBusy(true);
    setError(null);
    const reason = await useIdle.getState().switchWorld(f.worldId);
    setBusy(false);
    if (reason) setError(reason);
    else setSelected(null);
  };
  // D439: the open world's card says where it stands; the list carries nothing of the kind.
  const live = useIdle((s) => s.world);
  const t = useIdle((s) => s.now);
  const shown = live
    ? worlds.map((f) =>
        f.current && !f.archived
          ? {
              ...f,
              yearLine: yearHeader(live),
              money: live.year.coins,
              rate: idleIncome(live, idleContent, t),
            }
          : f,
      )
    : worlds;
  const world = shown.find((f) => f.worldId === selected && !f.archived);
  return (
    <>
      <WorldRows
        worlds={shown}
        limit={idleContent.worldsPerPlayer}
        onSelect={(f) => {
          setError(null);
          setSelected(f.worldId);
        }}
        onView={setViewing}
        onStart={() => useIdle.getState().setNewWorldOpen(true)}
        busy={busy}
      />
      {world && (
        <WorldSheet
          world={world}
          busy={busy}
          error={error}
          onOpen={() => void open(world)}
          onRename={() => setRenaming(true)}
          onDelete={() => setDeleting(world)}
          onClose={() => setSelected(null)}
        />
      )}
      {world && renaming && (
        <RenameWorldSheet
          name={world.name}
          worlds={worlds}
          worldId={world.worldId}
          onClose={() => setRenaming(false)}
        />
      )}
      {viewing && <ArchivedWorld world={viewing} onClose={() => setViewing(null)} />}
      {deleting && (
        <DeleteIdleWorld
          world={deleting}
          onClose={() => setDeleting(null)}
          onDone={() => {
            setDeleting(null);
            setSelected(null);
          }}
        />
      )}
    </>
  );
}

/**
 * D459: a world's own sheet, from its row: the same details, then Open (when it is not the one
 * playing), Rename and Delete. The row itself carries none of them.
 */
function WorldSheet({
  world,
  busy,
  error,
  onOpen,
  onRename,
  onDelete,
  onClose,
}: {
  world: IdleWorldEntry;
  busy: boolean;
  error: string | null;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet
      align="bottom"
      title={world.name}
      testId="world-row-sheet"
      onClose={onClose}
      zIndex={45}
      style={{ padding: 'var(--edge)' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <ThemeSwatch theme={world.theme} size={44} icon={22} />
        <WorldDetails f={world} full named={false} />
      </div>
      {error && (
        <p style={{ ...SMALL, color: 'var(--red)' }} role="alert">
          {error}
        </p>
      )}
      {!world.current && (
        <Button
          variant="primary"
          full
          data-testid={`world-open-${world.worldId}`}
          aria-label={`Open ${world.name}`}
          disabled={busy}
          onClick={onOpen}
        >
          Open
        </Button>
      )}
      <Button
        variant="secondary"
        full
        data-testid={`world-rename-${world.worldId}`}
        aria-label={`Rename ${world.name}`}
        disabled={busy}
        onClick={onRename}
      >
        Rename
      </Button>
      <Button
        variant="danger"
        full
        data-testid={`world-delete-${world.worldId}`}
        aria-label={deleteWorldLabel(world.name)}
        disabled={busy}
        onClick={onDelete}
      >
        {DELETE_WORLD}
      </Button>
    </Sheet>
  );
}

/** D337: delete an idle world, behind D221's typed-name confirmation. */
function DeleteIdleWorld({
  world,
  onClose,
  onDone,
}: {
  world: IdleWorldEntry;
  onClose: () => void;
  onDone: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    const reason = await useIdle.getState().deleteWorld(world.worldId);
    setBusy(false);
    if (reason) return setError(reason);
    onDone();
  };
  return (
    <Sheet
      align="bottom"
      title={deleteWorldTitle(world.name)}
      testId="delete-world-sheet"
      onClose={onClose}
      zIndex={60}
    >
      <DeleteWorldBody
        name={world.name}
        heritage={world.heritage ?? 0}
        loses={deleteIdleWorldLoses()}
        typed={typed}
        onType={setTyped}
        onConfirm={() => void confirm()}
        onCancel={onClose}
        busy={busy}
        error={error}
        confirmLabel={DELETE_WORLD_CONFIRM}
        inputLabel={DELETE_WORLD_INPUT}
        needsNameReason={DELETE_WORLD_NEEDS_NAME}
      />
    </Sheet>
  );
}

/** The read-only summary of a world from the old game. */
function ArchivedWorld({ world, onClose }: { world: IdleWorldEntry; onClose: () => void }) {
  const [view, setView] = useState<ArchivedWorldView | null | undefined>(undefined);
  // D267: Delete, behind D221's typed-name confirmation.
  const [deleting, setDeleting] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    const reason = await useIdle.getState().deleteWorld(world.worldId);
    setBusy(false);
    if (reason) return setError(reason);
    onClose();
  };
  useEffect(() => {
    let live = true;
    void idleSync.viewWorld(world.worldId).then((v) => {
      if (live) setView(v);
    });
    return () => {
      live = false;
    };
  }, [world.worldId]);
  const stage = view?.stage ?? world.stage;
  return (
    <Sheet title={world.name} onClose={onClose} testId="archived-world" zIndex={45}>
      <p style={{ margin: 0 }}>This world is archived. You can look but not play.</p>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px' }}>
        {stage !== undefined && (
          <>
            <dt style={SMALL}>Stage</dt>
            <dd style={{ margin: 0 }}>{stage}</dd>
          </>
        )}
        {world.worldValue !== undefined && (
          <>
            <dt style={SMALL}>World value</dt>
            <dd style={{ margin: 0 }}>{coins(world.worldValue)}</dd>
          </>
        )}
        <dt style={SMALL}>Last played</dt>
        <dd style={{ margin: 0 }}>
          {view === undefined
            ? 'Loading'
            : view?.lastPlayedAt
              ? day(view.lastPlayedAt)
              : 'Not known'}
        </dd>
      </dl>
      <Button
        variant="danger"
        full
        aria-label={deleteWorldLabel(world.name)}
        onClick={() => setDeleting(true)}
      >
        {DELETE_WORLD}
      </Button>
      {deleting && (
        <Sheet
          align="bottom"
          title={deleteWorldTitle(world.name)}
          testId="delete-world-sheet"
          onClose={() => setDeleting(false)}
          zIndex={50}
        >
          <DeleteWorldBody
            name={world.name}
            loses={deleteIdleWorldLoses()}
            confirmLabel={DELETE_WORLD_CONFIRM}
            inputLabel={DELETE_WORLD_INPUT}
            needsNameReason={DELETE_WORLD_NEEDS_NAME}
            typed={typed}
            onType={setTyped}
            onConfirm={() => void confirm()}
            onCancel={() => setDeleting(false)}
            busy={busy}
            error={error}
          />
        </Sheet>
      )}
    </Sheet>
  );
}
